import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
    Plus, 
    Users, 
    TrendingUp, 
    Clock, 
    CheckCircle2, 
    AlertCircle, 
    Info, 
    Briefcase, 
    User, 
    Lightbulb, 
    IndianRupee, 
    FileText, 
    MapPin, 
    Building, 
    Map, 
    Globe, 
    Building2, 
    Eye, 
    Link, 
    ExternalLink, 
    ArrowLeft, 
    Rocket 
} from 'lucide-react';
import { createJob } from '../services/jobService';
import { locationService } from '../services/locationService';
import { companyService } from '../services/companyService';
import RichTextEditor from '../components/jobs/RichTextEditor';

const AddJobPage = () => {
    const navigate = useNavigate();
    
    // Form state - keeping all original fields for backend compatibility
    const [formData, setFormData] = useState({
        jobTitle: '',
        jobType: '',
        remote: '',
        salary: '',
        descriptionOfJob: '',
        city: '',
        state: '',
        country: '',
        companyName: '',
        companyWebsite: ''
    });
    
    // UI state
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState(null);
    const [validatedFields, setValidatedFields] = useState(new Set());

    // Calculate form completion progress
    const getFormProgress = () => {
        const requiredFields = ['jobTitle', 'jobType', 'remote', 'city', 'state', 'country', 'companyName'];
        const filledFields = requiredFields.filter(field => formData[field]?.trim());
        const descriptionFilled = formData.descriptionOfJob && formData.descriptionOfJob.length > 50;
        return Math.round((filledFields.length + (descriptionFilled ? 1 : 0)) / (requiredFields.length + 1) * 100);
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
        
        // Add to validated fields if value exists
        if (value.trim()) {
            setValidatedFields(prev => new Set([...prev, name]));
        }
    };

    // Handle rich text editor change
    const handleDescriptionChange = (content) => {
        setFormData(prev => ({
            ...prev,
            descriptionOfJob: content
        }));
        
        // Clear error for description
        if (errors.descriptionOfJob) {
            setErrors(prev => ({
                ...prev,
                descriptionOfJob: null
            }));
        }

        // Add to validated fields if content exists
        if (content && content.trim() && content !== '<p><br></p>') {
            setValidatedFields(prev => new Set([...prev, 'descriptionOfJob']));
        }
    };

    // Auto-capitalize on blur
    const handleBlur = (e) => {
        const { name, value } = e.target;
        if (value && name !== 'salary') {
            const capitalizedValue = value.charAt(0).toUpperCase() + value.slice(1);
            setFormData(prev => ({
                ...prev,
                [name]: capitalizedValue
            }));
        }
    };

    // Validation
    const validateForm = () => {
        const newErrors = {};
        
        // Required fields validation
        const requiredFields = [
            { field: 'jobTitle', name: 'Job Title' },
            { field: 'jobType', name: 'Employment Type' },
            { field: 'remote', name: 'Work Arrangement' },
            { field: 'city', name: 'City' },
            { field: 'state', name: 'State' },
            { field: 'country', name: 'Country' },
            { field: 'companyName', name: 'Company Name' }
        ];
        
        requiredFields.forEach(({ field, name }) => {
            if (!formData[field] || !formData[field].trim()) {
                newErrors[field] = `${name} is required`;
            }
        });
        
        // Job description validation
        const description = formData.descriptionOfJob;
        if (!description || description === '<p><br></p>' || description.trim() === '' || description.length < 50) {
            newErrors.descriptionOfJob = 'Please provide a detailed job description (at least 50 characters)';
        }
        
        return newErrors;
    };

    // Show notification
    const showNotification = (message, type = 'info') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            showNotification('Please fill in all required fields correctly', 'error');
            
            // Scroll to first error
            const firstErrorField = Object.keys(validationErrors)[0];
            const errorElement = document.getElementById(firstErrorField);
            if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }
        
        try {
            setLoading(true);
            showNotification('Creating location and company records...', 'info');
            
            // Step 1: Create or get location
            // locationService returns ApiResponse<JobLocation>: { success, message, data: { id, city... } }
            const locationResponse = await locationService.createOrGetLocation({
                city: formData.city.trim(),
                state: formData.state.trim(),
                country: formData.country.trim()
            });

            // Use id == null (not !id) so that id=0 is treated as a valid identifier
            if (!locationResponse || !locationResponse.data || locationResponse.data.id == null) {
                throw new Error('location: Server did not return a valid location ID');
            }
            const locationId = locationResponse.data.id;
            console.log('Location created/found with ID:', locationId);
            
            // Step 2: Create or get JobCompany record
            // companyService returns ApiResponse<JobCompany>: { success, message, data: { id, name... } }
            const companyResponse = await companyService.createOrGetCompany({
                name: formData.companyName.trim(),
                website: formData.companyWebsite.trim() || null
            });

            // Use id == null (not !id) so that id=0 is treated as a valid identifier
            if (!companyResponse || !companyResponse.data || companyResponse.data.id == null) {
                throw new Error('company: Server did not return a valid company ID');
            }
            const companyId = companyResponse.data.id;
            console.log('Company created/found with ID:', companyId);
            
            showNotification('Posting your job...', 'info');
            
            // Step 3: Create job — send integer FK IDs, not name strings
            const jobData = {
                jobTitle: formData.jobTitle.trim(),
                jobType: formData.jobType,
                remote: formData.remote,
                salary: formData.salary.trim() || null,
                descriptionOfJob: formData.descriptionOfJob,
                jobLocationId: locationId,
                jobCompanyId: companyId
            };
            
            console.log('Submitting job data:', jobData);
            
            // jobService.createJob() swallows errors and returns { success, message, data }
            // Do NOT assume a non-throw means success — check the flag explicitly.
            const jobResponse = await createJob(jobData);
            console.log('Job create response:', jobResponse);

            // Ensure we handle various response structures
            const isSuccess = jobResponse && (jobResponse.success === true || jobResponse.jobPostId);
            
            if (!isSuccess) {
                const errMsg = jobResponse?.message || 'Failed to post job. Please try again.';
                showNotification(errMsg, 'error');
                return;
            }

            showNotification('Job posted successfully! Redirecting to dashboard...', 'success');
            setTimeout(() => {
                navigate('/dashboard');
            }, 2000);
            
        } catch (error) {
            console.error('Error creating job:', error);
            
            let errorMessage = 'Failed to post job. Please try again.';
            
            if (error.message && error.message.includes('location')) {
                errorMessage = 'Failed to create job location. Please check your location details.';
            } else if (error.message && error.message.includes('company')) {
                errorMessage = 'Failed to create company record. Please check your company details.';
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.status === 401) {
                errorMessage = 'You are not authorized to post jobs. Please log in again.';
            } else if (error.response?.status === 403) {
                errorMessage = 'You do not have permission to post jobs.';
            }
            
            showNotification(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };


    // Go back to dashboard
    const handleBack = () => {
        navigate('/dashboard');
    };

    const progress = getFormProgress();

    return (
        <div className="add-job-page">
            <Container className="py-4">
                {/* Enhanced Header with Animation */}
                <div className="job-posting-header mb-5">
                    <div className="header-background">
                        <div className="header-pattern"></div>
                    </div>
                    <div className="header-content text-center">
                        <div className="header-icon mb-3">
                            <div className="icon-circle">
                                <Plus size={32} />
                            </div>
                            <div className="icon-pulse"></div>
                        </div>
                        <h1 className="header-title mb-3">Post a New Job</h1>
                        <p className="header-subtitle mb-4">
                            Connect with top talent and build your dream team
                        </p>
                        
                        {/* Progress Indicator */}
                        <div className="progress-section mb-4">
                            <div className="progress-label mb-2">
                                <span>Form Completion</span>
                                <span className="progress-percentage">{progress}%</span>
                            </div>
                            <ProgressBar 
                                now={progress} 
                                className="custom-progress"
                                variant={progress < 30 ? 'danger' : progress < 70 ? 'warning' : 'success'}
                            />
                        </div>
                        
                        <div className="header-stats">
                            <div className="stat-item">
                                <div className="stat-icon">
                                    <Users size={18} />
                                </div>
                                <div className="stat-content">
                                    <span className="stat-number">15K+</span>
                                    <span className="stat-label">Active Job Seekers</span>
                                </div>
                            </div>
                            <div className="stat-divider"></div>
                            <div className="stat-item">
                                <div className="stat-icon">
                                    <TrendingUp size={18} />
                                </div>
                                <div className="stat-content">
                                    <span className="stat-number">92%</span>
                                    <span className="stat-label">Match Success Rate</span>
                                </div>
                            </div>
                            <div className="stat-divider"></div>
                            <div className="stat-item">
                                <div className="stat-icon">
                                    <Clock size={18} />
                                </div>
                                <div className="stat-content">
                                    <span className="stat-number">24h</span>
                                    <span className="stat-label">Average Response Time</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Notification Alert */}
                {notification && (
                    <Alert 
                        variant={notification.type === 'success' ? 'success' : notification.type === 'error' ? 'danger' : 'info'} 
                        className="notification-alert mb-4"
                        dismissible 
                        onClose={() => setNotification(null)}
                    >
                        <div className="alert-content">
                            <span className="alert-icon">
                                {notification.type === 'success' ? <CheckCircle2 size={18} /> : 
                                 notification.type === 'error' ? <AlertCircle size={18} /> : 
                                 <Info size={18} />}
                            </span>
                            <span>{notification.message}</span>
                        </div>
                    </Alert>
                )}

                {/* Enhanced Form with Modern Styling */}
                <div className="job-form-container">
                    <Form onSubmit={handleSubmit}>
                        {/* Job Basics Section */}
                        <Card className="form-section-card mb-4">
                            <Card.Body className="p-4">
                                <div className="section-header mb-4">
                                    <div className="section-icon-wrapper">
                                        <div className="section-icon">
                                            <Briefcase size={20} />
                                        </div>
                                    </div>
                                    <div className="section-content">
                                        <h3 className="section-title">Job Basics</h3>
                                        <p className="section-description">Start with the essential details about this position</p>
                                    </div>
                                </div>
                                
                                {/* Job Title */}
                                <div className="form-group-enhanced mb-4">
                                    <Form.Group>
                                        <Form.Label className="form-label-enhanced">
                                            Job Title <span className="required-indicator">*</span>
                                        </Form.Label>
                                        <div className="input-wrapper">
                                            <Form.Control
                                                id="jobTitle"
                                                type="text"
                                                name="jobTitle"
                                                value={formData.jobTitle}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                className={`form-control-enhanced ${
                                                    errors.jobTitle ? 'error' : 
                                                    validatedFields.has('jobTitle') ? 'success' : ''
                                                }`}
                                                placeholder="e.g. Senior Software Engineer, Marketing Manager, Data Scientist"
                                            />
                                            <div className="input-icon">
                                                <User size={16} />
                                            </div>
                                        </div>
                                        <div className="form-help d-flex align-items-center">
                                            <Lightbulb className="me-1" size={14} />
                                            Make it specific and clear to attract the right candidates
                                        </div>
                                        {errors.jobTitle && <div className="error-message">{errors.jobTitle}</div>}
                                    </Form.Group>
                                </div>

                                {/* Employment Type and Work Arrangement */}
                                <Row className="mb-4">
                                    <Col md={6}>
                                        <div className="form-group-enhanced">
                                            <Form.Group>
                                                <Form.Label className="form-label-enhanced">
                                                    Employment Type <span className="required-indicator">*</span>
                                                </Form.Label>
                                                <div className="select-wrapper">
                                                    <Form.Select
                                                        id="jobType"
                                                        name="jobType"
                                                        value={formData.jobType}
                                                        onChange={handleInputChange}
                                                        className={`form-select-enhanced ${
                                                            errors.jobType ? 'error' : 
                                                            validatedFields.has('jobType') ? 'success' : ''
                                                        }`}
                                                    >
                                                        <option value="">Choose employment type</option>
                                                        <option value="Full-time">Full-time</option>
                                                        <option value="Part-time">Part-time</option>
                                                        <option value="Freelance">Freelance</option>
                                                        <option value="Internship">Internship</option>
                                                    </Form.Select>
                                                </div>
                                                {errors.jobType && <div className="error-message">{errors.jobType}</div>}
                                            </Form.Group>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="form-group-enhanced">
                                            <Form.Group>
                                                <Form.Label className="form-label-enhanced">
                                                    Work Arrangement <span className="required-indicator">*</span>
                                                </Form.Label>
                                                <div className="select-wrapper">
                                                    <Form.Select
                                                        id="remote"
                                                        name="remote"
                                                        value={formData.remote}
                                                        onChange={handleInputChange}
                                                        className={`form-select-enhanced ${
                                                            errors.remote ? 'error' : 
                                                            validatedFields.has('remote') ? 'success' : ''
                                                        }`}
                                                    >
                                                        <option value="">Select work arrangement</option>
                                                        <option value="Remote-Only">Remote Only</option>
                                                        <option value="Office-Only">Office Only</option>
                                                        <option value="Partial-Remote">Hybrid (Remote + Office)</option>
                                                    </Form.Select>
                                                </div>
                                                {errors.remote && <div className="error-message">{errors.remote}</div>}
                                            </Form.Group>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Salary Range */}
                                <div className="form-group-enhanced mb-4">
                                    <Form.Group>
                                        <Form.Label className="form-label-enhanced">
                                            Salary Range <span className="optional-indicator">(Optional)</span>
                                        </Form.Label>
                                        <div className="input-wrapper">
                                            <Form.Control
                                                id="salary"
                                                type="text"
                                                name="salary"
                                                value={formData.salary}
                                                onChange={handleInputChange}
                                                className="form-control-enhanced"
                                                placeholder="e.g. ₹30,000 - ₹50,000 per month, ₹5,00,000 - ₹8,00,000 per year, Competitive"
                                            />
                                            <div className="input-icon">
                                                <IndianRupee size={16} />
                                            </div>
                                        </div>
                                        <div className="form-help success d-flex align-items-center">
                                            <TrendingUp className="me-1" size={14} />
                                            Including salary ranges can increase application rates by 30%
                                        </div>
                                    </Form.Group>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Job Description Section */}
                        <Card className="form-section-card mb-4">
                            <Card.Body className="p-4">
                                <div className="section-header mb-4">
                                    <div className="section-icon-wrapper">
                                        <div className="section-icon">
                                            <FileText size={20} />
                                        </div>
                                    </div>
                                    <div className="section-content">
                                        <h3 className="section-title">Job Description</h3>
                                        <p className="section-description">Describe the role, responsibilities, and requirements in detail</p>
                                    </div>
                                </div>
                                
                                <div className="form-group-enhanced">
                                    <Form.Group>
                                        <Form.Label className="form-label-enhanced">
                                            Full Job Description <span className="required-indicator">*</span>
                                        </Form.Label>
                                        <div className="editor-wrapper">
                                            <RichTextEditor
                                                value={formData.descriptionOfJob}
                                                onChange={handleDescriptionChange}
                                                placeholder="Describe the role in detail...

• Key responsibilities and daily tasks
• Required qualifications and skills  
• Preferred experience and background
• Benefits, perks, and compensation
• Company culture and values
• Growth opportunities"
                                                className={`editor-enhanced ${
                                                    errors.descriptionOfJob ? 'error' : 
                                                    validatedFields.has('descriptionOfJob') ? 'success' : ''
                                                }`}
                                            />
                                        </div>
                                        <div className="form-help d-flex align-items-center">
                                            <Lightbulb className="me-1" size={14} />
                                            Include key responsibilities, required skills, benefits, and company culture
                                        </div>
                                        {errors.descriptionOfJob && <div className="error-message">{errors.descriptionOfJob}</div>}
                                    </Form.Group>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Location Section */}
                        <Card className="form-section-card mb-4">
                            <Card.Body className="p-4">
                                <div className="section-header mb-4">
                                    <div className="section-icon-wrapper">
                                        <div className="section-icon">
                                            <MapPin size={20} />
                                        </div>
                                    </div>
                                    <div className="section-content">
                                        <h3 className="section-title">Job Location</h3>
                                        <p className="section-description">Where will this position be based?</p>
                                    </div>
                                </div>
                                
                                <Row>
                                    <Col md={4}>
                                        <div className="form-group-enhanced mb-4">
                                            <Form.Group>
                                                <Form.Label className="form-label-enhanced">
                                                    City <span className="required-indicator">*</span>
                                                </Form.Label>
                                                <div className="input-wrapper">
                                                    <Form.Control
                                                        id="city"
                                                        type="text"
                                                        name="city"
                                                        value={formData.city}
                                                        onChange={handleInputChange}
                                                        onBlur={handleBlur}
                                                        className={`form-control-enhanced ${
                                                            errors.city ? 'error' : 
                                                            validatedFields.has('city') ? 'success' : ''
                                                        }`}
                                                        placeholder="e.g. New York"
                                                    />
                                                    <div className="input-icon">
                                                        <Building size={16} />
                                                    </div>
                                                </div>
                                                {errors.city && <div className="error-message">{errors.city}</div>}
                                            </Form.Group>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="form-group-enhanced mb-4">
                                            <Form.Group>
                                                <Form.Label className="form-label-enhanced">
                                                    State/Province <span className="required-indicator">*</span>
                                                </Form.Label>
                                                <div className="input-wrapper">
                                                    <Form.Control
                                                        id="state"
                                                        type="text"
                                                        name="state"
                                                        value={formData.state}
                                                        onChange={handleInputChange}
                                                        onBlur={handleBlur}
                                                        className={`form-control-enhanced ${
                                                            errors.state ? 'error' : 
                                                            validatedFields.has('state') ? 'success' : ''
                                                        }`}
                                                        placeholder="e.g. NY, California"
                                                    />
                                                    <div className="input-icon">
                                                        <Map size={16} />
                                                    </div>
                                                </div>
                                                {errors.state && <div className="error-message">{errors.state}</div>}
                                            </Form.Group>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="form-group-enhanced mb-4">
                                            <Form.Group>
                                                <Form.Label className="form-label-enhanced">
                                                    Country <span className="required-indicator">*</span>
                                                </Form.Label>
                                                <div className="input-wrapper">
                                                    <Form.Control
                                                        id="country"
                                                        type="text"
                                                        name="country"
                                                        value={formData.country}
                                                        onChange={handleInputChange}
                                                        onBlur={handleBlur}
                                                        className={`form-control-enhanced ${
                                                            errors.country ? 'error' : 
                                                            validatedFields.has('country') ? 'success' : ''
                                                        }`}
                                                        placeholder="e.g. United States"
                                                    />
                                                    <div className="input-icon">
                                                        <Globe size={16} />
                                                    </div>
                                                </div>
                                                {errors.country && <div className="error-message">{errors.country}</div>}
                                            </Form.Group>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Company Information Section */}
                        <Card className="form-section-card mb-4">
                            <Card.Body className="p-4">
                                <div className="section-header mb-4">
                                    <div className="section-icon-wrapper">
                                        <div className="section-icon">
                                            <Building2 size={20} />
                                        </div>
                                    </div>
                                    <div className="section-content">
                                        <h3 className="section-title">Company Information</h3>
                                        <p className="section-description">Tell candidates about your organization</p>
                                    </div>
                                </div>
                                
                                <Row>
                                    <Col md={8}>
                                        <div className="form-group-enhanced mb-4">
                                            <Form.Group>
                                                <Form.Label className="form-label-enhanced">
                                                    Company Name <span className="required-indicator">*</span>
                                                </Form.Label>
                                                <div className="input-wrapper">
                                                    <Form.Control
                                                        id="companyName"
                                                        type="text"
                                                        name="companyName"
                                                        value={formData.companyName}
                                                        onChange={handleInputChange}
                                                        onBlur={handleBlur}
                                                        className={`form-control-enhanced ${
                                                            errors.companyName ? 'error' : 
                                                            validatedFields.has('companyName') ? 'success' : ''
                                                        }`}
                                                        placeholder="e.g. Google Inc., Microsoft Corporation"
                                                    />
                                                    <div className="input-icon">
                                                        <Building2 size={16} />
                                                    </div>
                                                </div>
                                                <div className="form-help d-flex align-items-center">
                                                    <Eye className="me-1" size={14} />
                                                    This will be displayed prominently on the job listing
                                                </div>
                                                {errors.companyName && <div className="error-message">{errors.companyName}</div>}
                                            </Form.Group>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="form-group-enhanced mb-4">
                                            <Form.Group>
                                                <Form.Label className="form-label-enhanced">
                                                    Company Website <span className="optional-indicator">(Optional)</span>
                                                </Form.Label>
                                                <div className="input-wrapper">
                                                    <Form.Control
                                                        id="companyWebsite"
                                                        type="url"
                                                        name="companyWebsite"
                                                        value={formData.companyWebsite}
                                                        onChange={handleInputChange}
                                                        className="form-control-enhanced"
                                                        placeholder="https://company.com"
                                                    />
                                                    <div className="input-icon">
                                                        <Link size={16} />
                                                    </div>
                                                </div>
                                                <div className="form-help d-flex align-items-center">
                                                    <ExternalLink className="me-1" size={14} />
                                                    Help candidates learn more about your company
                                                </div>
                                            </Form.Group>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Form>
                </div>

                {/* Enhanced Action Footer */}
                <Card className="action-footer-card">
                    <Card.Body className="p-4">
                        <div className="action-footer">
                            <div className="footer-left">
                                <Button 
                                    variant="outline-secondary" 
                                    className="btn-back-enhanced d-flex align-items-center"
                                    onClick={handleBack}
                                    disabled={loading}
                                >
                                    <ArrowLeft size={18} className="me-2" />
                                    <span>Back to Dashboard</span>
                                </Button>
                            </div>
                            
                            <div className="footer-center">
                                <div className="reach-info d-flex align-items-center">
                                    <div className="reach-icon me-2">
                                        <Users size={18} />
                                    </div>
                                    <div className="reach-content">
                                        <span className="reach-text">Reach over</span>
                                        <span className="reach-number">15,000+</span>
                                        <span className="reach-text">qualified candidates</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="footer-right">
                                <Button 
                                    type="submit" 
                                    className="btn-post-enhanced d-flex align-items-center"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            <span>Posting Job...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Rocket size={18} className="me-2" />
                                            <span>Post Job Now</span>
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Container>

            <style jsx>{`
                .add-job-page {
                    background: linear-gradient(135deg, #34C1D9 0%, #1A8FA3 100%);
                    min-height: 100vh;
                    padding: 2rem 0;
                }

                .job-posting-header {
                    position: relative;
                    margin-bottom: 3rem;
                }

                .header-background {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%);
                    border-radius: 20px;
                    backdrop-filter: blur(10px);
                    box-shadow: 0 20px 40px rgba(0,0,0,0.1);
                }

                .header-pattern {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-image: 
                        radial-gradient(circle at 20% 50%, rgba(120,119,198,0.1) 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, rgba(255,119,198,0.1) 0%, transparent 50%),
                        radial-gradient(circle at 40% 80%, rgba(120,200,255,0.1) 0%, transparent 50%);
                    border-radius: 20px;
                }

                .header-content {
                    position: relative;
                    z-index: 2;
                    padding: 3rem 2rem;
                }

                .header-icon {
                    position: relative;
                    display: inline-block;
                    margin-bottom: 1.5rem;
                }

                .icon-circle {
                    width: 80px;
                    height: 80px;
                    background: linear-gradient(135deg, #34C1D9 0%, #1A8FA3 100%);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 2rem;
                    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
                    animation: iconFloat 3s ease-in-out infinite;
                }

                .icon-pulse {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 80px;
                    height: 80px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #34C1D9 0%, #1A8FA3 100%);
                    opacity: 0.3;
                    animation: pulse 2s infinite;
                }

                @keyframes iconFloat {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-10px); }
                }

                @keyframes pulse {
                    0% { transform: scale(1); opacity: 0.3; }
                    50% { transform: scale(1.2); opacity: 0.1; }
                    100% { transform: scale(1.4); opacity: 0; }
                }

                .header-title {
                    font-size: 2.5rem;
                    font-weight: 700;
                    background: linear-gradient(135deg, #34C1D9 0%, #1A8FA3 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    margin-bottom: 1rem;
                }

                .header-subtitle {
                    font-size: 1.2rem;
                    color: #6b7280;
                    font-weight: 400;
                    max-width: 600px;
                    margin: 0 auto 2rem;
                }

                .progress-section {
                    max-width: 400px;
                    margin: 0 auto 2rem;
                }

                .progress-label {
                    display: flex;
                    justify-content: space-between;
                    font-size: 0.9rem;
                    color: #6b7280;
                    font-weight: 500;
                }

                .progress-percentage {
                    color: #34C1D9;
                    font-weight: 600;
                }

                .custom-progress {
                    height: 8px;
                    border-radius: 10px;
                    background-color: rgba(255,255,255,0.3);
                    overflow: hidden;
                }

                .custom-progress .progress-bar {
                    border-radius: 10px;
                    transition: all 0.3s ease;
                }

                .header-stats {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 2rem;
                    flex-wrap: wrap;
                    margin-top: 2rem;
                }

                .stat-item {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .stat-icon {
                    width: 40px;
                    height: 40px;
                    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #34C1D9;
                    font-size: 1.1rem;
                }

                .stat-content {
                    display: flex;
                    flex-direction: column;
                }

                .stat-number {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1f2937;
                    line-height: 1;
                }

                .stat-label {
                    font-size: 0.8rem;
                    color: #6b7280;
                    font-weight: 500;
                }

                .stat-divider {
                    width: 1px;
                    height: 40px;
                    background: rgba(0,0,0,0.1);
                    margin: 0 1rem;
                }

                .notification-alert {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
                    backdrop-filter: blur(10px);
                }

                .alert-content {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .alert-icon {
                    font-size: 1.1rem;
                }

                .job-form-container {
                    max-width: 900px;
                    margin: 0 auto;
                }

                .form-section-card {
                    border: none;
                    border-radius: 16px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                    backdrop-filter: blur(10px);
                    background: rgba(255,255,255,0.95);
                    overflow: hidden;
                    transition: all 0.3s ease;
                }

                .form-section-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 15px 40px rgba(0,0,0,0.15);
                }

                .section-header {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    margin-bottom: 2rem;
                }

                .section-icon-wrapper {
                    position: relative;
                }

                .section-icon {
                    width: 50px;
                    height: 50px;
                    background: linear-gradient(135deg, #34C1D9 0%, #1A8FA3 100%);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 1.2rem;
                    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
                }

                .section-content h3 {
                    color: #1f2937;
                    font-size: 1.5rem;
                    font-weight: 600;
                    margin: 0 0 0.5rem 0;
                }

                .section-description {
                    color: #6b7280;
                    font-size: 0.95rem;
                    margin: 0;
                }

                .form-group-enhanced {
                    margin-bottom: 1.5rem;
                }

                .form-label-enhanced {
                    color: #374151;
                    font-weight: 600;
                    font-size: 0.95rem;
                    margin-bottom: 0.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .required-indicator {
                    color: #ef4444;
                    font-weight: 700;
                }

                .optional-indicator {
                    color: #64748b;
                    font-weight: 400;
                    font-size: 0.85rem;
                }

                .input-wrapper, .select-wrapper {
                    position: relative;
                }

                .form-control-enhanced, .form-select-enhanced {
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 0.75rem 1rem;
                    padding-left: 3rem;
                    font-size: 0.95rem;
                    transition: all 0.3s ease;
                    background: #ffffff;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                .form-control-enhanced:focus, .form-select-enhanced:focus {
                    border-color: #34C1D9;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                    outline: none;
                }

                .form-control-enhanced.success, .form-select-enhanced.success {
                    border-color: #34C1D9;
                    background-color: rgba(16, 185, 129, 0.05);
                }

                .form-control-enhanced.error, .form-select-enhanced.error {
                    border-color: #ef4444;
                    background-color: rgba(239, 68, 68, 0.05);
                }

                .input-icon {
                    position: absolute;
                    left: 1rem;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #9ca3af;
                    font-size: 0.9rem;
                    z-index: 2;
                }

                .form-help {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-top: 0.5rem;
                    font-size: 0.85rem;
                    color: #6b7280;
                }

                .form-help.success {
                    color: #2AA8BD;
                }

                .form-help i {
                    font-size: 0.8rem;
                }

                .error-message {
                    color: #ef4444;
                    font-size: 0.85rem;
                    margin-top: 0.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .error-message::before {
                    content: "⚠️";
                    font-size: 0.8rem;
                }

                .editor-wrapper {
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    overflow: hidden;
                    transition: all 0.3s ease;
                    background: #ffffff;
                }

                .editor-wrapper:focus-within {
                    border-color: #34C1D9;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                }

                .editor-enhanced.success .editor-wrapper {
                    border-color: #34C1D9;
                }

                .editor-enhanced.error .editor-wrapper {
                    border-color: #ef4444;
                }

                .action-footer-card {
                    border: none;
                    border-radius: 16px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                    backdrop-filter: blur(10px);
                    background: rgba(255,255,255,0.95);
                    position: sticky;
                    bottom: 2rem;
                    z-index: 10;
                }

                .action-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 2rem;
                    flex-wrap: wrap;
                }

                .footer-left, .footer-right {
                    flex: 0 0 auto;
                }

                .footer-center {
                    flex: 1;
                    display: flex;
                    justify-content: center;
                }

                .btn-back-enhanced {
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 0.75rem 1.5rem;
                    font-weight: 600;
                    background: #ffffff;
                    color: #374151;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    transition: all 0.3s ease;
                }

                .btn-back-enhanced:hover {
                    border-color: #9ca3af;
                    background: #f9fafb;
                    transform: translateY(-1px);
                }

                .reach-info {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
                    padding: 0.75rem 1.5rem;
                    border-radius: 12px;
                    border: 1px solid rgba(102, 126, 234, 0.2);
                }

                .reach-icon {
                    color: #34C1D9;
                    font-size: 1.1rem;
                }

                .reach-content {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    font-size: 0.9rem;
                }

                .reach-text {
                    color: #6b7280;
                }

                .reach-number {
                    color: #34C1D9;
                    font-weight: 700;
                    font-size: 1rem;
                }

                .btn-post-enhanced {
                    background: linear-gradient(135deg, #34C1D9 0%, #1A8FA3 100%);
                    border: none;
                    border-radius: 12px;
                    padding: 0.75rem 2rem;
                    font-weight: 600;
                    color: white;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    transition: all 0.3s ease;
                    box-shadow: 0 8px 20px rgba(52, 193, 217, 0.3);
                }

                .btn-post-enhanced:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 25px rgba(52, 193, 217, 0.4);
                    background: linear-gradient(135deg, #4DD8F0 0%, #2AB6CE 100%);
                }

                .btn-post-enhanced:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
                }

                @media (max-width: 768px) {
                    .header-title {
                        font-size: 2rem;
                    }
                    
                    .header-stats {
                        gap: 1rem;
                    }
                    
                    .stat-divider {
                        display: none;
                    }
                    
                    .action-footer {
                        flex-direction: column;
                        gap: 1rem;
                    }
                    
                    .footer-center {
                        order: -1;
                    }
                }
            `}</style>
        </div>
    );
};

export default AddJobPage;